
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPerformanceMatrixHoverText } from "../../utils/organisationDashboard";
import { ResponsiveContainer } from "recharts";
import {
  COLORS,
  TYPES_OF_COMPETATIVE_METRICS,
  TYPES_OF_METRICS,
} from "../../constants/metrics";
import { getClientTrend } from "../../actions/client/clientActions";
import InterpretationModal from "../InterpretationModal";
import Loader from "../Loader/Loader";

const Patterns = ({
  setCurrentType,
  currentMetric,
  setCurrentMetric,
  setPatternData,
  metricColors,
  setMetricColors,
  parentScrollRef,
  setCompetitiveTrendLabel
}) => {
  const parentRef = useRef(null);
  const childRef = useRef(null);

  const dispatch = useDispatch();
  const [isInterpretationMeodalOpen, setIsInterpretationModalOpen] =
    useState(false);
  const [interpretationModalText, setInterpretationModalText] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [varianceData, setVarianceData] = useState(null);
  const [data, setData] = useState(null);
  const [selectedCell, setSelectedCell] = useState();
  const [selectedresMatLang] = useState("english");
  const [highlightedRow, setHighlightedRow] = useState("");
  const [highlightedColumn, setHighlightedColumn] = useState("");
  const [hoverTextColor, setHoverTextColor] = useState("");
  const { loading: clientLoading, data: clientData } = useSelector(
    (state) => state.clientReducer
  );
  const { loading: zScoreLoading, data: zScore } = useSelector(
    (state) => state.clientZScoreReducer
  );
  const { loading: trendLoading, data: trend  } = useSelector(
    (state) => state.clientTrendReducer
  );

  useEffect(() => {
    if (!clientLoading && clientData?._id) {
      dispatch(getClientTrend(clientData?._id));
    }
  }, [dispatch, clientLoading]);
  
  useEffect(() => {
    if (
      !clientLoading &&
      clientData?._id &&
      zScore?.length > 0 &&
      !trendLoading
    ) {
      const years = [...clientData?.auditUploadYears];
      const currentYear = years?.sort((a, b) => b - a)[0];
      const currentYearData = zScore?.filter((item) => {
        if (Number(item?.year) === Number(currentYear)) {
          return true;
        }
        return false;
      });
      const varianceData = currentYearData[0]?.metrics;
      setVarianceData(varianceData);

      const result = {
        "Very High": {
          falling: [],
          stagnant: [],
          rising: [],
        },
        High: {
          falling: [],
          stagnant: [],
          rising: [],
        },
        Moderate: {
          falling: [],
          stagnant: [],
          rising: [],
        },
        Low: {
          falling: [],
          stagnant: [],
          rising: [],
        },
        "Very Low": {
          falling: [],
          stagnant: [],
          rising: [],
        },
      };
      const competitiveMenuLabel = {};
      for (const variable in varianceData) {
        const zscoreLabel = varianceData[variable].label;
        const trendCategory = trend[variable] || "";
        result[zscoreLabel][trendCategory]?.push(variable);
        competitiveMenuLabel[variable] = {
          trendCategory: trendCategory,
          label: zscoreLabel,
        };
      }
      setData({ ...result });
      setCompetitiveTrendLabel({ ...competitiveMenuLabel });
    }
  }, [dispatch, zScore, trend]);

  useEffect(() => {
    if (!varianceData || !trend) return;
    const metricPatternColors = TYPES_OF_COMPETATIVE_METRICS.flatMap(
      (topLevel) =>
        topLevel.value.flatMap((subCategory) =>
          subCategory.value.map((metric) => metric.value)
        )
    );

    const res = metricPatternColors.reduce((obj, val) => {
      const key = val.charAt(0).toLowerCase() + val.slice(1);
      const situation = varianceData[key]?.label || '';
      const trendOfKey = trend[key];

      const res = getPerformanceMatrixHoverText(
        selectedresMatLang,
        key,
        situation,
        trendOfKey
      )?.[0];

      obj[val] = COLORS?.[res.color];

      return obj;
    }, {});

    setMetricColors(res);
  }, [varianceData, trend, trendLoading]);

  useEffect(() => {
    const findRowColumn = () => {
      let foundIn = null;

      Object.keys(data).forEach((category) => {
        Object.keys(data[category]).forEach((status) => {
          if (data[category][status].includes(currentMetric)) {
            foundIn = { category, status };
          }
        });
      });

      setHighlightedRow(foundIn?.category);
      setHighlightedColumn(foundIn?.status);
    };

    data && currentMetric && findRowColumn();
  }, [data, currentMetric]);

  useEffect(() => {
    if (!currentMetric || !varianceData || !trend) return;

    const inputString = currentMetric;
    const key = inputString.charAt(0).toLowerCase() + inputString.slice(1);
    const situation = varianceData[key]?.label;
    const trendOfKey = trend[key];

    const res = getPerformanceMatrixHoverText(
      selectedresMatLang,
      key,
      situation,
      trendOfKey
    )?.[0];

    setPatternData({
      pattern: `${situation} - ${trendOfKey}`,
      interpretation: res,
    });
  }, [currentMetric, varianceData, trend]);

  const handleMouseOver = (e, textColor, cellIndex) => {
    if (parentRef.current && childRef.current) {
      const parentRect = parentRef.current.getBoundingClientRect();
      const childRect = childRef.current.getBoundingClientRect();

      // Calculate the position relative to the parent
      const relativeX = childRect.left - parentRect.left;
      const relativeY =
        childRect.top - parentRect.top + parentScrollRef.current.scrollTop;
      var offsetX = 0;
      var offsetY = 0;
      if (cellIndex % 3 === 1) {
        offsetX = relativeX - 50;
        offsetY = relativeY - 150;
      } else if (cellIndex % 3 === 2) {
        offsetX = relativeX - 150;
        offsetY = relativeY - 150;
      } else if (cellIndex % 3 === 0) {
        offsetX = relativeX - 50;
        offsetY = relativeY - 150;
      }
      setMousePosition({ x: offsetX, y: offsetY });
    }
    setHoverTextColor(textColor);

    const inputString = e.currentTarget.dataset.metric || "";
    const key = inputString.charAt(0).toLowerCase() + inputString.slice(1);
    const situation = varianceData[key].label;
    const trendOfKey = trend[key];

    setInterpretationModalText(
      getPerformanceMatrixHoverText(
        selectedresMatLang,
        key,
        situation,
        trendOfKey
      )
    );
    setIsInterpretationModalOpen(true);
  };

  const handleMouseOut = (e) => {
    setIsInterpretationModalOpen(false);
  };

  return (
    <Box style={{ width: "100%" }}>
      <Box
        py={5}
        px={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text textTransform="uppercase" fontWeight={600} fontSize={18}>
          METRICS' RELATIVE PATTERN
        </Text>
      </Box>
      {trendLoading ? (
        <>
          <Box h={"200px"}>
            <Loader />
          </Box>
        </>
      ) : (
        <Box>
          {trend && "debtRatio" in trend ? (
            <Box overflowX="auto">
              <table 
                id="resource-matrix-table" 
                ref={parentRef}
                style={{ 
                  width: "100%", 
                  borderCollapse: "collapse",
                  fontFamily: "Arial, sans-serif"
                }}
              >
                <thead>
                  <tr>
                    <th style={{ 
                      padding: "12px", 
                      border: "1px solid #E2E8F0",
                      backgroundColor: "#F7FAFC",
                      fontWeight: "bold",
                      textAlign: "left"
                    }}></th>
                    {["Falling", "Stagnant", "Rising"].map((header, index) => (
                      <th 
                        key={header}
                        style={{ 
                          padding: "12px", 
                          border: "1px solid #E2E8F0",
                          backgroundColor: "#F7FAFC",
                          fontWeight: "bold",
                          textAlign: "center",
                          color: highlightedColumn === header.toLowerCase() ? "#2D3748" : "#718096"
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {["Very High", "High", "Moderate", "Low", "Very Low"].map((row, rowIndex) => (
                    <tr key={row}>
                      <th style={{ 
                        padding: "12px", 
                        border: "1px solid #E2E8F0",
                        backgroundColor: "#F7FAFC",
                        fontWeight: "bold",
                        textAlign: "left",
                        color: highlightedRow === row ? "#2D3748" : "#718096"
                      }}>
                        {row}
                      </th>
                      
                      {["falling", "stagnant", "rising"].map((status, colIndex) => {
                        const cellIndex = rowIndex * 3 + colIndex + 1;
                        return (
                          <td 
                            key={status} 
                            ref={selectedCell === cellIndex ? childRef : null}
                            style={{ 
                              padding: "12px", 
                              border: "1px solid #E2E8F0",
                              textAlign: "center",
                              minWidth: "120px",
                              height: "80px",
                              verticalAlign: "middle",
                              backgroundColor: highlightedRow === row && highlightedColumn === status ? "#EDF2F7" : "white"
                            }}
                          >
                            <Flex wrap="wrap" justify="center">
                              {data?.[row]?.[status]?.map((item, index) => (
                                <Box
                                  key={index}
                                  display="inline-block"
                                  m={1}
                                  px={3}
                                  py={1}
                                  fontSize="12px"
                                  fontWeight="500"
                                  borderRadius="4px"
                                  border="1px solid"
                                  borderColor={currentMetric === item ? "#2C5282" : "#CBD5E0"}
                                  bg={currentMetric === item ? "#2C5282" : "white"}
                                  color={currentMetric === item ? "white" : "#2D3748"}
                                  cursor="pointer"
                                  _hover={{
                                    bg: currentMetric === item ? "#2C5282" : "#E2E8F0",
                                  }}
                                  onMouseEnter={(e) => {
                                    setSelectedCell(cellIndex);
                                    handleMouseOver(e, metricColors?.[item]?.textColor, cellIndex);
                                  }}
                                  onMouseOut={handleMouseOut}
                                  data-metric={item}
                                  onClick={() => {
                                    setCurrentMetric(item);
                                    Object.keys(TYPES_OF_METRICS).map((key) => {
                                      if (
                                        TYPES_OF_METRICS[key].value.some(
                                          (val) => val.label === item
                                        )
                                      ) {
                                        setCurrentType(key);
                                      }
                                    });
                                  }}
                                >
                                  {TYPES_OF_METRICS?.marginAndCost?.value?.some?.(
                                    (m) => m?.label === item
                                  )
                                    ? `${item} %`
                                    : item}
                                </Box>
                              ))}
                            </Flex>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>

              {isInterpretationMeodalOpen && (
                <InterpretationModal
                  position={mousePosition}
                  text={interpretationModalText}
                  textColor={hoverTextColor}
                />
              )}
            </Box>
          ) : (
            <ResponsiveContainer aspect={1.2} textAlign={"center"}>
              <Box
                bg="#B3A7FA1A"
                w="100%"
                p={4}
                color="white"
                display="flex"
                alignItems="center"
              >
                <Heading
                  color={"primary.500"}
                  w={"full"}
                  size={"md"}
                  textAlign={"center"}
                >
                  No data available
                </Heading>
              </Box>
            </ResponsiveContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Patterns;